'use client'

import { useState } from 'react'
import { elements } from '@/data/elements'
import { Button } from './ui/button'
import { Calculator, Info } from 'lucide-react'

interface ElementCount {
  symbol: string
  count: number
  atomicWeight: number
  name: string
}

export function MolarMassCalculator() {
  const [formula, setFormula] = useState('')
  const [result, setResult] = useState<{
    total: number
    breakdown: ElementCount[]
    error?: string
  } | null>(null)

  const parseFormula = (input: string): { breakdown: ElementCount[], total: number } | { error: string } => {
    const elementMap = new Map(elements.map(el => [el.symbol, el]))
    const breakdown: Map<string, ElementCount> = new Map()

    // Remove spaces
    const cleanFormula = input.replace(/\s/g, '')

    // Pattern: Element symbol (uppercase + optional lowercase) followed by optional number
    const pattern = /([A-Z][a-z]?)(\d*)/g
    let match

    let hasValidElement = false

    while ((match = pattern.exec(cleanFormula)) !== null) {
      const symbol = match[1]
      const countStr = match[2]
      const count = countStr ? parseInt(countStr, 10) : 1

      if (!symbol) continue

      const element = elementMap.get(symbol)
      if (!element) {
        return { error: `Élément inconnu: ${symbol}` }
      }

      hasValidElement = true

      if (breakdown.has(symbol)) {
        const existing = breakdown.get(symbol)!
        existing.count += count
      } else {
        breakdown.set(symbol, {
          symbol,
          count,
          atomicWeight: element.atomicWeight,
          name: element.name
        })
      }
    }

    if (!hasValidElement) {
      return { error: 'Formule invalide. Exemples: H2O, C6H12O6, NaCl' }
    }

    const breakdownArray = Array.from(breakdown.values())
    const total = breakdownArray.reduce((sum, el) => sum + (el.atomicWeight * el.count), 0)

    return { breakdown: breakdownArray, total }
  }

  const handleCalculate = () => {
    if (!formula.trim()) {
      setResult({ total: 0, breakdown: [], error: 'Entrez une formule chimique' })
      return
    }

    const parsed = parseFormula(formula)

    if ('error' in parsed) {
      setResult({ total: 0, breakdown: [], error: parsed.error })
    } else {
      setResult({ total: parsed.total, breakdown: parsed.breakdown })
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCalculate()
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-6 bg-[#112240] border-2 border-[#233554] rounded-xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#64ffda] mb-2 flex items-center gap-2">
          <Calculator size={28} />
          Calculateur de Masse Molaire
        </h2>
        <p className="text-[#a8b2d1] text-sm">
          Entrez une formule chimique pour calculer sa masse molaire
        </p>
      </div>

      {/* Input */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ex: H2O, C6H12O6, NaCl..."
            className="flex-1 px-4 py-3 bg-[#0a192f] border-2 border-[#233554] rounded-lg text-white placeholder-[#a8b2d1] focus:outline-none focus:border-[#64ffda] transition-colors font-mono text-lg"
          />
          <Button
            onClick={handleCalculate}
            className="bg-[#64ffda] text-[#0a192f] hover:bg-[#64ffda]/90 px-6"
          >
            <Calculator size={18} className="mr-2" />
            Calculer
          </Button>
        </div>
      </div>

      {/* Examples */}
      <div className="mb-6 flex flex-wrap gap-2">
        <span className="text-sm text-[#a8b2d1]">Exemples:</span>
        {['H2O', 'CO2', 'C6H12O6', 'NaCl', 'H2SO4', 'CaCO3'].map(ex => (
          <button
            key={ex}
            onClick={() => setFormula(ex)}
            className="px-3 py-1 bg-[#0a192f] border border-[#233554] rounded-md text-[#64ffda] hover:bg-[#233554] transition-colors text-sm font-mono"
          >
            {ex}
          </button>
        ))}
      </div>

      {/* Result */}
      {result && (
        <div className="mt-6">
          {result.error ? (
            <div className="p-4 bg-red-500/10 border-2 border-red-500 rounded-lg">
              <p className="text-red-500 font-semibold">{result.error}</p>
            </div>
          ) : (
            <>
              {/* Total */}
              <div className="p-6 bg-[#64ffda]/10 border-2 border-[#64ffda] rounded-lg mb-4">
                <p className="text-sm text-[#a8b2d1] mb-1">Masse molaire totale</p>
                <p className="text-4xl font-bold text-[#64ffda]">
                  {result.total.toFixed(3)} <span className="text-2xl">g/mol</span>
                </p>
              </div>

              {/* Breakdown */}
              <div className="p-4 bg-[#0a192f] border-2 border-[#233554] rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={16} className="text-[#64ffda]" />
                  <h3 className="text-lg font-semibold text-white">Détail du calcul</h3>
                </div>
                <div className="space-y-2">
                  {result.breakdown.map((el, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-[#112240] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-[#64ffda]">
                          {el.symbol}
                          {el.count > 1 && <sub className="text-lg">{el.count}</sub>}
                        </span>
                        <span className="text-white">{el.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-[#a8b2d1] text-sm">
                          {el.atomicWeight.toFixed(3)} × {el.count} =
                        </p>
                        <p className="text-white font-semibold">
                          {(el.atomicWeight * el.count).toFixed(3)} g/mol
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
